export type RouteOrderStop = {
  id: string;
  name: string;
  routeIds: Array<string | number>;
  nextStopsByRoute?: Record<string, string[]>;
};

/** Returns a complete sequence only when provider next-stop edges describe one unambiguous chain. */
export function providerOrderedRouteStops(stops: RouteOrderStop[], routeId: string): RouteOrderStop[] | null {
  const routeStops = stops.filter((stop) => stop.routeIds.some((id) => String(id) === routeId));
  if (routeStops.length === 0) return null;

  const stopsByName = new Map<string, RouteOrderStop[]>();
  for (const stop of routeStops) stopsByName.set(stop.name, [...(stopsByName.get(stop.name) ?? []), stop]);

  const nextById = new Map<string, string[]>();
  const incoming = new Map(routeStops.map((stop) => [stop.id, 0]));
  for (const stop of routeStops) {
    const nextNames = stop.nextStopsByRoute?.[routeId] ?? [];
    const nextIds: string[] = [];
    for (const name of nextNames) {
      const matches = stopsByName.get(name) ?? [];
      if (matches.length !== 1) return null;
      const nextId = matches[0].id;
      if (nextId === stop.id) continue;
      if (!nextIds.includes(nextId)) nextIds.push(nextId);
    }
    if (nextIds.length > 1) return null;
    nextById.set(stop.id, nextIds);
    for (const nextId of nextIds) {
      const count = (incoming.get(nextId) ?? 0) + 1;
      if (count > 1) return null;
      incoming.set(nextId, count);
    }
  }

  const heads = routeStops.filter((stop) => incoming.get(stop.id) === 0);
  if (heads.length !== 1) return null;
  const ordered: RouteOrderStop[] = [];
  const visited = new Set<string>();
  let current: RouteOrderStop | undefined = heads[0];
  while (current && !visited.has(current.id)) {
    ordered.push(current);
    visited.add(current.id);
    current = routeStops.find((stop) => stop.id === nextById.get(current!.id)?.[0]);
  }
  return ordered.length === routeStops.length ? ordered : null;
}
